/**
 * High-Quality Pixel Art Animal Avatars for ClearCommunicationApp
 * 
 * DINO and LLAMA use inline SVG pixel grids (exact match from timer)
 * Other 18 avatars use professionally generated PNG images
 * MILESTONE AVATARS unlock based on achievements
 */

// EXACT pixel data from DinoLlamaTimerPage.jsx
// Values: 0=empty, 1=dino color, 2=white (eye)
export const DINO_PIXELS = [
  [0,0,0,0,0,0,1,1,1,1,1,1],
  [0,0,0,0,0,1,1,1,1,1,1,1,1],
  [0,0,0,0,0,1,1,2,1,1,1,1,1],
  [0,0,0,0,0,1,1,1,1,1,1,1],
  [0,0,0,0,0,1,1,1,1,0,0,0],
  [0,0,0,1,0,1,1,1,1,1,0,0],
  [0,0,1,1,1,1,1,1,1,1,0,0],
  [0,1,1,1,1,1,1,1,1,0,0,0],
  [1,1,1,1,1,1,1,1,0,0,0,0],
  [0,1,1,1,1,1,1,1,0,0,0,0],
  [0,0,1,1,1,1,1,0,0,0,0,0],
  [0,0,0,1,1,0,1,1,0,0,0,0],
  [0,0,0,1,0,0,0,1,0,0,0,0],
  [0,0,0,1,1,0,0,0,0,0,0,0],
];

export const LLAMA_PIXELS = [
  [0,0,0,1,0,1,0,0],
  [0,0,0,1,1,1,0,0],
  [0,0,0,0,1,0,0,0],
  [0,0,0,0,1,0,0,0],
  [0,0,0,0,1,0,0,0],
  [0,0,1,1,1,1,0,0],
  [0,1,1,1,1,1,1,0],
  [0,1,1,1,1,1,1,0],
  [0,0,1,1,1,1,0,0],
  [0,0,1,0,0,1,0,0],
  [0,0,1,0,0,1,0,0],
  [0,0,1,0,0,1,0,0],
];

export const DINO_COLOR = '#00E5CC';  // Aquamarine
export const LLAMA_COLOR = '#7C3AED'; // Purple

// Milestone avatar colors
export const GOLD_DINO_COLOR = '#FFD700';      // Gold - 30 day streak
export const DIAMOND_LLAMA_COLOR = '#B9F2FF';  // Diamond blue - 100 day streak
export const FIRE_DINO_COLOR = '#FF6B35';      // Fire orange - 7 day streak
export const RAINBOW_LLAMA_COLOR = '#FF69B4';  // Pink - VIP subscriber

// Milestone requirements for exclusive avatars
export const MILESTONE_AVATARS = {
  'fire_dino': { 
    requirement: 'streak_7', 
    checkUnlocked: (user) => (user?.longest_streak || 0) >= 7,
    descriptionEn: 'Unlock with 7-day streak',
    descriptionEs: 'Desbloquea con racha de 7 días'
  },
  'gold_dino': { 
    requirement: 'streak_30', 
    checkUnlocked: (user) => (user?.longest_streak || 0) >= 30,
    descriptionEn: 'Unlock with 30-day streak',
    descriptionEs: 'Desbloquea con racha de 30 días'
  },
  'diamond_llama': { 
    requirement: 'streak_100', 
    checkUnlocked: (user) => (user?.longest_streak || 0) >= 100,
    descriptionEn: 'Unlock with 100-day streak',
    descriptionEs: 'Desbloquea con racha de 100 días'
  },
  'rainbow_llama': { 
    requirement: 'vip', 
    checkUnlocked: (user) => {
      // Check if user has VIP subscription via tier or status
      const hasVipTier = user?.subscription_tier && user.subscription_tier !== 'free';
      const hasActiveStatus = user?.subscription_status === 'active' || user?.subscription_status === 'trial';
      return hasVipTier || hasActiveStatus;
    },
    descriptionEn: 'VIP Exclusive',
    descriptionEs: 'Exclusivo VIP'
  },
  'champion_dino': { 
    requirement: 'streak_365', 
    checkUnlocked: (user) => (user?.longest_streak || 0) >= 365,
    descriptionEn: 'Unlock with 365-day streak',
    descriptionEs: 'Desbloquea con racha de 365 días'
  },
  'scholar_owl': {
    requirement: 'phrases_500',
    checkUnlocked: (user) => (user?.phrases_viewed || 0) >= 500,
    descriptionEn: 'Unlock by viewing 500 phrases',
    descriptionEs: 'Desbloquea viendo 500 frases'
  }
};

// Avatar options with names and image URLs
export const AVATAR_OPTIONS = [
  { 
    id: 'dino', 
    name: 'Detective Dino', 
    nameEs: 'Dino Detective',
    isPixelSprite: true,  // Uses inline SVG pixel grid
    pixels: DINO_PIXELS,
    color: DINO_COLOR
  },
  { 
    id: 'llama', 
    name: 'Lucky Llama', 
    nameEs: 'Llama Afortunada',
    isPixelSprite: true,  // Uses inline SVG pixel grid
    pixels: LLAMA_PIXELS,
    color: LLAMA_COLOR
  },
  // === MILESTONE AVATARS (locked by default) ===
  { 
    id: 'fire_dino', 
    name: '🔥 Fire Dino', 
    nameEs: '🔥 Dino de Fuego',
    isPixelSprite: true,
    pixels: DINO_PIXELS,
    color: FIRE_DINO_COLOR,
    isMilestone: true,
    milestoneId: 'fire_dino'
  },
  { 
    id: 'gold_dino', 
    name: '⭐ Gold Dino', 
    nameEs: '⭐ Dino Dorado',
    isPixelSprite: true,
    pixels: DINO_PIXELS,
    color: GOLD_DINO_COLOR,
    isMilestone: true,
    milestoneId: 'gold_dino'
  },
  { 
    id: 'diamond_llama', 
    name: '💎 Diamond Llama', 
    nameEs: '💎 Llama Diamante',
    isPixelSprite: true,
    pixels: LLAMA_PIXELS,
    color: DIAMOND_LLAMA_COLOR,
    isMilestone: true,
    milestoneId: 'diamond_llama'
  },
  { 
    id: 'rainbow_llama', 
    name: '👑 Rainbow Llama', 
    nameEs: '👑 Llama Arcoíris',
    isPixelSprite: true,
    pixels: LLAMA_PIXELS,
    color: RAINBOW_LLAMA_COLOR,
    isMilestone: true,
    milestoneId: 'rainbow_llama'
  },
  { 
    id: 'champion_dino', 
    name: '🏆 Champion Dino', 
    nameEs: '🏆 Dino Campeón',
    isPixelSprite: true,
    pixels: DINO_PIXELS,
    color: '#9333EA',  // Royal purple
    isMilestone: true,
    milestoneId: 'champion_dino'
  },
  // === REGULAR AVATARS ===
  { 
    id: 'platypus', 
    name: 'Venomous Platypus', 
    nameEs: 'Ornitorrinco Venenoso',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/ff7227f8d7f398b74a14484874f16cd8cb3b6ce34888ec08b6c5f88fce20a919.png'
  },
  { 
    id: 'penguin', 
    name: 'Penguin Pal', 
    nameEs: 'Pingüino Amigo',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/240c09316c51357a5a110d6a4246a6cb654742d6db1ac0506976457f25d3bfec.png'
  },
  { 
    id: 'cheetah', 
    name: "Chillin' Cheetah", 
    nameEs: 'Guepardo Relajado',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/525be412ab2526079c674274714442256594ef7a8439601f892802f30dd12395.png'
  },
  { 
    id: 'cichlid', 
    name: 'Amazing African Cichlid', 
    nameEs: 'Cíclido Africano Asombroso',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/52b7f53ba7730eca6edf84b20efad89249157aac2dcdbae617e97c22ce978884.png'
  },
  { 
    id: 'possum', 
    name: 'Possum near Oceania', 
    nameEs: 'Possum de Oceanía',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/d2a3980c1b3090cd4f92ddb3031193b7a735ee976f887af0b8f80d40fc90fb08.png'
  },
  { 
    id: 'opossum', 
    name: 'Optimistic Opossum', 
    nameEs: 'Zarigüeya Optimista',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/54d9cd6b14e535fb76b57eca38d5cbe5f21130fd93bf86987da28c86c4cf0886.png'
  },
  { 
    id: 'kangaroo', 
    name: 'Kangaroo Kid', 
    nameEs: 'Canguro Pequeño',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/bad5edf9fbbdb407c4de871081bce627245bf337b4a811c619c79d9421df9aee.png'
  },
  { 
    id: 'duck', 
    name: 'Dazzling Quacker', 
    nameEs: 'Pato Deslumbrante',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/f529f6b009a761cb10f3ae04ce97eb9df92628a893ab981cc9d41898a80e6fa0.png'
  },
  { 
    id: 'okapi', 
    name: 'Okapi Loves Onomatopoeia', 
    nameEs: 'Okapi Ama la Onomatopeya',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/45e80345ed759723bfe79fb11d73fb5226ed122ee15c9fe87e13f2b8bed9eb49.png'
  },
  { 
    id: 'hawk', 
    name: 'Helpful Hawk', 
    nameEs: 'Halcón Servicial',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/765718bce2a126b1210d24d5eef4206b7da0a2c3bc3b1f80007f0eeef19ca021.png'
  },
  { 
    id: 'owl', 
    name: 'Owl See You Later (Hoo)', 
    nameEs: 'Búho Te Veo Luego (Huu)',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/37ad4eea963f909a1d91ea6ee8df4a9377acb7c82448eee874f664d5b68c955a.png'
  },
  { 
    id: 'cow', 
    name: 'Moo Cow', 
    nameEs: 'Vaca Muu',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/29091622c7bb9732caf9e0ebbbe2eded26c36aa658a3fc462b1c549046ce2358.png'
  },
  { 
    id: 'raccoon', 
    name: 'Resourceful Raccoon', 
    nameEs: 'Mapache Ingenioso',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/f3d4b13b2f9110d762117cf1a00dcd576f3908e6af4ddcf23b7a25bf7a2b8ed3.png'
  },
  { 
    id: 'zebra', 
    name: 'Zinnia Loving Zebra', 
    nameEs: 'Cebra Amante de Zinnias',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/cf4aab233fb8afc6362683dfb99d47b1ce53672a89b7ba7519c85ab76adf4268.png'
  },
  { 
    id: 'cat', 
    name: 'Curious Cat', 
    nameEs: 'Gato Curioso',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/097c710c3c3159f5618fbe42a166a0bf54559c84d2cd5bf46675defc9b48e76e.png'
  },
  { 
    id: 'corgi', 
    name: 'Cheerful Corgi', 
    nameEs: 'Corgi Alegre',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/a93989e3c828eb7a293cc9e3b6ef8e156a2bdb0548b975e9335668c6bb38cc2a.png'
  },
  { 
    id: 'beardeddragon', 
    name: 'Brave Bearded Dragon', 
    nameEs: 'Dragón Barbudo Valiente',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/e2ec1d49674707bdd7edc4bd296b4372fe4faff4d40ed6ab4c85de0d3faf0be3.png'
  },
  { 
    id: 'alpaca', 
    name: 'Adorable Alpaca', 
    nameEs: 'Alpaca Adorable',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/7deddbd347d8df9b557eaf9f332418b472c354fac0264201c5d57ba9b19371ce.png'
  },
  // Scholar Owl - unlocks at 500 phrases viewed
  { 
    id: 'scholar_owl', 
    name: '🎓 Scholar Owl', 
    nameEs: '🎓 Búho Erudito',
    imageUrl: 'https://static.prod-images.emergentagent.com/jobs/7835de73-c89d-4de4-af93-ede5a9de6a85/images/37ad4eea963f909a1d91ea6ee8df4a9377acb7c82448eee874f664d5b68c955a.png',
    isMilestone: true,
    milestoneId: 'scholar_owl'
  },
];

// Helper function to get avatar by ID
export const getAvatarById = (id) => {
  return AVATAR_OPTIONS.find(avatar => avatar.id === id) || AVATAR_OPTIONS[0];
};

// Default avatar
export const DEFAULT_AVATAR = 'dino';

// Get avatar image URL by ID (for non-pixel sprite avatars)
export const getAvatarImageUrl = (id) => {
  const avatar = getAvatarById(id);
  return avatar?.imageUrl || null;
};

// Check if a milestone avatar is unlocked for a user
export const isAvatarUnlocked = (avatarId, user) => {
  const avatar = getAvatarById(avatarId);
  if (!avatar?.isMilestone) return true; // Non-milestone avatars are always unlocked
  
  const milestone = MILESTONE_AVATARS[avatar.milestoneId];
  if (!milestone) return true;
  
  return milestone.checkUnlocked(user);
};

// Get all unlocked avatars for a user
export const getUnlockedAvatars = (user) => {
  return AVATAR_OPTIONS.filter(avatar => isAvatarUnlocked(avatar.id, user));
};

// Get locked milestone avatars with requirements
export const getLockedMilestoneAvatars = (user) => {
  return AVATAR_OPTIONS
    .filter(avatar => avatar.isMilestone && !isAvatarUnlocked(avatar.id, user))
    .map(avatar => ({
      ...avatar,
      milestone: MILESTONE_AVATARS[avatar.milestoneId]
    }));
};
